import {
	Currency,
	DocumentReference,
	Entity,
	Timestamp,
} from '@marmon/shared/types/common';
import { User } from '@marmon/shared/types/user';
import { AnalysisCalculation } from './analysis-calculation.interface';
import { ParseInvoiceError } from './invoice-error.interface';
import { TimePeriod } from './time-period.interface';

export const CalculationEntityTypes = ['customers', 'products'] as const;
export type CalculationEntityType = (typeof CalculationEntityTypes)[number];

export const AnalysisInvoiceEntityTypes = [
	...CalculationEntityTypes,
	'segments',
] as const;

export type AnalysisInvoiceEntityType =
	(typeof AnalysisInvoiceEntityTypes)[number];

export const AnalysisCostTypes = ['direct', 'material'] as const;

/**
 * Each analysis can have a different cost type. The options are either
 * direct or material. If direct we can assume additional calculations
 * such as contribution margin. If the cost is material, then we cannot
 * determine contribution margin effectively so we show the user material
 * margin instead.
 *
 * @note we will still make the calculations for contribution margin and append
 * it to the invoice object before storing into the database. This is to avoid additional
 * calculations of the user has made an error here and needed to change this cost
 * type in the future.
 */
export type AnalysisCostType = (typeof AnalysisCostTypes)[number];
export type AnalysisStatus =
	| 'deleted'
	| 'uploading'
	| 'error'
	| 'ready'
	| 'expired';

export const AnalysisUploadSections = [
	'invoice',
	'config',
	'product',
	'customer',
	'plot',
] as const;

export type AnalysisUploadSection = (typeof AnalysisUploadSections)[number];

export type AnalysisUploadSectionData = {
	/**
	 * the current index of the total aggregated data count of the section
	 */
	current: number;
	/**
	 * the total amount of data to upload for the section
	 */
	total: number;
	/**
	 * `true` if the upload section has completed
	 */
	completed: boolean;
};

export type AnalysisUploadDataSections = {
	[key in AnalysisUploadSection]: AnalysisUploadSectionData;
};

export interface AnalysisUploadProgress extends AnalysisUploadSectionData {
	sections: AnalysisUploadDataSections;

	/**
	 * timestamp of when the last time this checkpoint was saved
	 */
	lastSavedAt: Timestamp | null;
}

interface AnalysisBase extends Entity {
	name: string;
	timePeriods: [TimePeriod, TimePeriod];
	/**
	 * Email host of the creating user, including TLD (`vizashift.com`).
	 */
	domain: string;

	segments: string[];

	costType: AnalysisCostType;

	/**
	 * When an analysis has been created, we run the calculations for all of the default values and
	 * reference it here for a quick grab when needed.
	 *
	 * @note the default calculation will be of `revenue` type.
	 */
	defaultCalculation: DocumentReference<AnalysisCalculation>;

	/**
	 * Display currency. Monetary amounts stay in cents and are not converted
	 * when this value changes.
	 */
	currency: Currency;

	/**
	 * The time stamp that the analysis needs to be expired and data purged from the system.
	 * We could possibly keep the data entity around, but the data encapsulated in the nested
	 * collection should be wiped and no longer usable.
	 */
	expiresAt: Timestamp;

	/**
	 * All of the emails of the users that this analysis is shared with
	 */
	shared: DocumentReference<User>[];

	/**
	 * the timestamp at which the analysis was purged of all it's data. This could happen either
	 * when the user explicitly "deletes" an analysis so all the invoices are purged, or if an
	 * invoice expires and all of the invoices are purged automatically. This is for audit related
	 * purposes.
	 */
	purgedAt: Timestamp | null;

	/**
	 * Firebase Storage object path for the original invoice CSV
	 * (`{ownerId}/analyses/{analysisId}/{uuid}.csv`).
	 * Present on analyses created after CSV retention; omitted on older docs.
	 *
	 * When set, `CalculationService` parses this file instead of reading the
	 * invoices subcollection. That path **requires** `createInvoiceId()` so
	 * rebuilt rows update the same Firestore invoice docs import created.
	 * Do not add `csvStoragePath` without deterministic ids, and do not change
	 * the id fingerprint without updating both import and CSV-sourced calcs.
	 */
	csvStoragePath?: string;

	/**
	 * the amount of transactions that are included with this analysis.
	 */
	transactionCount: number;

	/**
	 * the timestamp at which the analysis had an error detecting in the upload process. This could have
	 * happened for a multitude of reasons with the parsing or size of the file. Pre-processing the file
	 * should help alleviate this from being set, but it may not stop everything.
	 *
	 * @todo: save errors generated from the backend to be displayed to the user
	 */
	erroredAt: Timestamp | null;

	/**
	 * The timestamp of when the analysis finished processing and is no able to be used.
	 */
	processedAt: Timestamp | null;

	createdBy: DocumentReference<User>;

	/**
	 * List of errors that occurred while trying to process the analysis
	 *
	 * @note we only take the first 10 errors due to the size possibly being very large
	 */
	errors?: never | ParseInvoiceError[];
	errorCount?: never | number;

	/**
	 * During an analysis upload, there can be issues that cause a restart in the uploading process.
	 * when this happens we need to make sure the progress continues from when it last started.
	 *
	 * @note
	 * should only be available during the analysis upload and error status. This should be removed
	 * if the analysis has completed without errors
	 */
	uploadProgress?: AnalysisUploadProgress | never;
}

interface AnalysisProcessing extends AnalysisBase {
	uploadProgress: AnalysisUploadProgress;

	processedAt: null;
	erroredAt: null;

	errors?: never;
	errorCount?: never;
}

interface AnalysisWithErrors extends AnalysisBase {
	processedAt: null;
	erroredAt: Timestamp;
	errors: ParseInvoiceError[];
	errorCount: number;
	uploadProgress: AnalysisUploadProgress;
}

interface AnalysisWithoutErrors extends AnalysisBase {
	erroredAt: null;
	errors?: never;
	errorCount?: never;
	uploadProgress?: never;
}

export type Analysis =
	| AnalysisProcessing
	| AnalysisWithoutErrors
	| AnalysisWithErrors;
